import { Checkbox, Modal } from "antd";
import { useEffect, useState } from "react";

import { WORKER_LEFT_EARLY_REASON_TYPE } from "../../../../constants/shifts";
import { getWorkerShiftTypeLabel } from "../../../../appV2/redesign/utils/worker";
import { ShiftCancellationModalTitle } from "../../../shiftCancellationModalTitle";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { WleFormContent } from "@src/appV2/redesign/Shift/components/WleFormContent";
import { useSelector } from "react-redux";
import { SessionType } from "src/modules/interface";
import { useWorkerLeftEarlyFeatureFlag } from "./useWorkerLeftEarlyFeatureFlag";
import { ShiftDescription } from "../../../shiftDescription";

import { Shift } from "src/interface";
import "./style.scss";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { isDefined, isNullOrUndefined, toError } from "@clipboard-health/util-ts";
import { workerLeftEarlyErrorSchema } from "../markShiftWorkerLeftEarly";
import { UrgencyType } from "@src/appV2/Shifts/types";

export interface WorkerLeftEarlyModalProps {
  shift?: Pick<
    Shift,
    | "_id"
    | "agent"
    | "agentId"
    | "start"
    | "facility"
    | "instantPayDetails"
    | "agentReq"
    | "urgency"
    | "end"
    | "time"
    | "name"
  >;
  visible: boolean;
  onConfirm: (
    reasonType: string,
    reasonDescription: string,
    requestReplacement?: boolean,
    permissionGiven?: boolean
  ) => Promise<unknown>;
  onClose: () => Promise<void>;
}

interface SuperAgentError extends Error {
  response?: Response;
  status?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSuperAgentRequestError(error: any): error is SuperAgentError {
  return error && typeof error === "object" && "response" in error;
}

export function WorkerLeftEarlyModal(props: WorkerLeftEarlyModalProps): JSX.Element {
  const { shift, visible, onConfirm, onClose } = props;
  const [reasonDescription, setReasonDescription] = useState<string>("");
  const [permissionGiven, setPermissionGiven] = useState<boolean | undefined>(undefined);
  const [requestReplacement, setRequestReplacement] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { admin } = useSelector((state: SessionType) => state.session);
  const featureFlag = useWorkerLeftEarlyFeatureFlag(shift?.end);

  useEffect(() => {
    setReasonDescription("");
    setPermissionGiven(undefined);
    setRequestReplacement(false);
    setLoading(false);
  }, [visible]);

  const onRequestReplacementChange = (e: CheckboxChangeEvent): void => {
    setRequestReplacement(e.target.checked);
  };

  const workerName = shift?.agent?.name;
  const permissionYesLabel = admin
    ? `Yes, workplace is okay with ${workerName || "the worker"} leaving the shift early.`
    : `Yes, and I am aware and okay with ${workerName ?? "the worker"} leaving the shift early as they had a valid reason. This worker will not incur any penalities on their account.`;
  const permissionNoLabel = admin
    ? `No, workplace did not give permission for ${workerName || "the worker"} to leave the shift early.`
    : `No, and I understand that the ${workerName || "the worker"} will incur penalities on their account for shift abandonment as they did not fulfill their responsibilities.`;

  const onConfirmWorkerLeftEarly = async (): Promise<void> => {
    if (!reasonDescription) {
      showErrorToast("Please describe why the worker is leaving the shift early.");
      return;
    }

    if (isNullOrUndefined(permissionGiven)) {
      showErrorToast("Please select whether permission was given for the worker to leave early.");
      return;
    }

    setLoading(true);

    try {
      await onConfirm(
        WORKER_LEFT_EARLY_REASON_TYPE,
        reasonDescription,
        requestReplacement,
        permissionGiven
      );
    } catch (error) {
      if (isSuperAgentRequestError(error)) {
        const errorMessage = workerLeftEarlyErrorSchema.parse(error.response?.body);
        if (errorMessage) {
          showErrorToast(errorMessage.errors.map((error_) => error_.detail).join("\n"));
        } else {
          showErrorToast(toError(error)?.message);
        }
      } else {
        showErrorToast(toError(error)?.message);
      }
      // eslint-disable-next-line no-console
      console.error(error);
    }

    setLoading(false);
  };

  return (
    <Modal
      className="worker-left-early-modal"
      width={600}
      title={<ShiftCancellationModalTitle title="" />}
      okText="Submit"
      cancelText="Back"
      visible={visible}
      onOk={onConfirmWorkerLeftEarly}
      onCancel={onClose}
      okButtonProps={{
        loading,
        disabled: isNullOrUndefined(permissionGiven) || !reasonDescription || loading,
      }}
      destroyOnClose={true}
    >
      <div>
        {shift?.agent && (
          <h3 className="modal-worker-name">
            {shift.agent.name},{" "}
            {getWorkerShiftTypeLabel(shift.agentReq, shift.facility?.fullAddress?.state)}
          </h3>
        )}
        <ShiftDescription shift={shift} isCancel={false} />

        <WleFormContent
          reasonDescription={reasonDescription}
          permissionGiven={permissionGiven}
          permissionYesLabel={permissionYesLabel}
          permissionNoLabel={permissionNoLabel}
          onReasonDescriptionChange={setReasonDescription}
          onPermissionGivenChange={setPermissionGiven}
        />

        {featureFlag.replacementFlow.enabled &&
          featureFlag.canRequestReplacement &&
          (!isDefined(shift?.urgency) || shift.urgency !== UrgencyType.NCNS) && (
            <>
              <p className="modal-question">
                {admin
                  ? "Should we post a replacement shift?"
                  : "Do you need an Urgent Shift posted?"}
              </p>

              <Checkbox onChange={onRequestReplacementChange} checked={requestReplacement}>
                {admin
                  ? `Workplace requested a replacement worker`
                  : `Yes, I'd like to request a replacement worker at my standard rates with no additional rush fee.`}
              </Checkbox>
            </>
          )}
      </div>
    </Modal>
  );
}
